/* eslint-disable camelcase */
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { type ComponentProps, useCallback, useEffect, useState } from "react";
import { type WorkWithShopWorkCassette } from "@userFrontend/features/publication/components/WorkWithShopWorkCassette";
import { type WorkWithShopWorkCassetteProps } from "@userFrontend/features/publication/components/WorkWithShopWorkCassette/type";
import { PHONE_ENTRY_TEL_NUMBER } from "@root/shared/utils/constants";
import { axiosPost } from "@root/shared/utils/axiosForRails";
import { type AxiosError } from "axios";
import {
  chooseSalaryUnit,
  omitEmploymentName,
  formatSalaryPriceRound,
} from "@root/userFrontend/utils/formatSalary";
import { type SecondaryContentPostProps } from "@userFrontend/features/publication/types/Cassettes";
import {
  type PrimaryContentPostProps,
  type PrimarySearchHitCassettes,
} from "@userFrontend/features/publication/components/WorkWithShopWorkCassettes/type";

type ResponseProps = {
  data: PrimarySearchHitCassettes[];
};

type UseWorkWithShopWorkCassettesParams = {
  url: PrimaryContentPostProps["url"];
  params: PrimaryContentPostProps["params"];
  isLogin: boolean;
  secondaryContentAsync: SecondaryContentPostProps;
};

export const useWorkWithShopWorkCassettes = ({
  url,
  params,
  isLogin,
  secondaryContentAsync,
}: UseWorkWithShopWorkCassettesParams): {
  workWithShopWorks: ComponentProps<typeof WorkWithShopWorkCassette>[];
} => {
  const [worksData, setWorksData] = useState<PrimarySearchHitCassettes[]>([]);

  const fetchWorksData = useCallback(async () => {
    await axiosPost
      .post(url, params)
      .then((res: ResponseProps) => {
        setWorksData(res.data);
      })
      .catch((error: AxiosError) => {
        sendErrorLog(new Error(error.message || "求人カセットのデータを取得できませんでした"));
      });
  }, [url, params]);

  useEffect(() => {
    fetchWorksData();
  }, [fetchWorksData]);

  // 受け取ったデータを、使用するコンポーネントのpropsに合わせて整形する
  const formatData = useCallback(() => {
    if (!worksData.length) return [];

    const formattedData: WorkWithShopWorkCassetteProps[] = worksData.map((work) => {
      const matchedClientShopId = /.*_(.*)$/.exec(work.docId);
      const gaLabel = `${work.workCode}_${matchedClientShopId ? matchedClientShopId[1] : "null"}`;

      const latestAwardLabel = work.awardsLabels.sort((a, b) => b.year - a.year);

      const employmentAndSalaryType = work.salaries.map((salary) => ({
        employmentName: omitEmploymentName(salary.employmentTypeName),
        salaryMax: formatSalaryPriceRound(salary.maxPrice),
        salaryMaxUnit: chooseSalaryUnit(salary.maxPrice),
        salaryMin: formatSalaryPriceRound(salary.minPrice),
        salaryMinUnit: chooseSalaryUnit(salary.minPrice),
        salaryTypeName: salary.salaryTypeName,
      }));

      const shopInformations = work.hitOtherShopWorks.map((shopInformation) => ({
        detailUrl: shopInformation.detailUrl,
        shopName: shopInformation.shopName,
        prefecture: shopInformation.shopAddress.prefecture,
        city: shopInformation.shopAddress.city,
        detailAddress: shopInformation.shopAddress.detailAddress,
        detailNearestStation: shopInformation.shopAddress.station,
        docId: shopInformation.docId,
      }));

      // 店舗モーダルに店舗がまったく表示されない場合はnewrelicにエラー通知する
      if (work.hitOtherShopWorks.length === 0 && work.hitShopWorkCount > 1) {
        sendErrorLog(new Error(`店舗を取得できませんでした URL：${window.location.href}`));
      }

      // ピンポイント上位求人がある場合を考慮して、現在のwork.docIdと一致するものをparamsの中から探す
      const aggregateKey = getMatchedAggregateKey(
        params.pinpoint_high_rank_hit_shop_works,
        params.search_hit_shop_works,
        work.docId,
      );
      // 上記でaggregateKeyを取得できないパターンは通常起こり得ないが、万が一発生した場合はnewrelicにエラー通知する
      if (!aggregateKey) {
        sendErrorLog(new Error(`aggregate_keyを取得できませんでした URL：${window.location.href}`));
      }

      const formattedShopWorks = () => {
        const otherShopWorks = work.hitOtherShopWorks.map((shopInformation) => {
          const shopEmploymentAndSalaryType = shopInformation.salaries.map((shopSalary) => ({
            employmentName: omitEmploymentName(shopSalary.employmentTypeName),
            salaryMax: formatSalaryPriceRound(shopSalary.maxPrice),
            salaryMaxUnit: chooseSalaryUnit(shopSalary.maxPrice),
            salaryMin: formatSalaryPriceRound(shopSalary.minPrice),
            salaryMinUnit: chooseSalaryUnit(shopSalary.minPrice),
            salaryTypeName: shopSalary.salaryTypeName,
          }));
          const shopWorkCode = () => {
            const matchedWorkCode = /job\/([^/]+)\/sid(?:.*)$/.exec(shopInformation.detailUrl);
            return matchedWorkCode ? matchedWorkCode[1] : "null";
          };

          return {
            docId: shopInformation.docId,
            imgSrc: shopInformation.imgSrc,
            url: shopInformation.detailUrl,
            title: shopInformation.title,
            shopName: shopInformation.shopName,
            shopAddress: {
              prefecture: shopInformation.shopAddress.prefecture,
              city: shopInformation.shopAddress.city,
              station: shopInformation.shopAddress.station,
            },
            employmentAndSalaryType: shopEmploymentAndSalaryType,
            enterableFlag: shopInformation.actionContent.entry.enterable,
            entriedFlag: shopInformation.actionContent.entry.hasEntered,
            scoutedFlag: shopInformation.actionContent.scoutApprove.scoutedFlag,
            scoutedEntryUrl: shopInformation.actionContent.scoutApprove.scoutedEntryUrl,
            workCode: shopWorkCode(),
            query: work.query,
            entryUrl: shopInformation.actionContent.entry.url,
          };
        });
        const currentShopWorks = {
          docId: work.docId,
          imgSrc: work.imgSrc,
          url: work.detailUrl,
          title: work.title,
          shopName: work.shopName,
          shopAddress: {
            prefecture: work.shopAddress.prefecture,
            city: work.shopAddress.city,
            station: work.shopAddress.station,
          },
          employmentAndSalaryType: work.salaries.map((shopSalary) => ({
            employmentName: omitEmploymentName(shopSalary.employmentTypeName),
            salaryMax: formatSalaryPriceRound(shopSalary.maxPrice),
            salaryMaxUnit: chooseSalaryUnit(shopSalary.maxPrice),
            salaryMin: formatSalaryPriceRound(shopSalary.minPrice),
            salaryMinUnit: chooseSalaryUnit(shopSalary.minPrice),
            salaryTypeName: shopSalary.salaryTypeName,
          })),
          enterableFlag: work.actionContent.entry.enterable,
          entriedFlag: work.actionContent.entry.hasEntered,
          scoutedFlag: work.actionContent.scoutApprove.scoutedFlag,
          scoutedEntryUrl: work.actionContent.scoutApprove.scoutedEntryUrl,
          workCode: work.workCode,
          query: work.query,
          entryUrl: work.actionContent.entry.url,
        };
        return [currentShopWorks, ...otherShopWorks];
      };

      const formattedWork: WorkWithShopWorkCassetteProps = {
        aggregateKey: aggregateKey || "",
        docId: work.docId,
        statusLabelFlags: work.statusLabelFlags,
        imgSrc: work.imgSrc,
        brandName: work.brandName,
        businessTypeAndOccupation: `${work.businessType} / ${work.occupation}`,
        detailUrl: work.detailUrl,
        shopName: work.shopName,
        title: work.title,
        isRejobAgentClient: work.isRejobAgentClient,
        awardsLabel: latestAwardLabel.length
          ? {
              rank: latestAwardLabel[0].rank,
              children: latestAwardLabel[0].displayTextShort,
            }
          : undefined,
        isReviewLabel: work.hasReview,
        featureLabels: work.featureLabels,
        employmentAndSalaryType,
        isLogin,
        keepButtonUrl: work.actionContent.keep.url,
        hasKept: work.actionContent.keep.hasKept,
        enterableFlag: work.actionContent.entry.enterable,
        entriedFlag: work.actionContent.entry.hasEntered,
        scoutedFlag: work.actionContent.scoutApprove.scoutedFlag,
        scoutedEntryUrl: work.actionContent.scoutApprove.scoutedEntryUrl,
        scoutDeadline: work.actionContent.scoutApprove.scoutDeadlineDays,
        workCode: work.workCode,
        query: work.query,
        entryUrl: work.actionContent.entry.url,
        gaLabel,
        telNumberContent: {
          telNumber: PHONE_ENTRY_TEL_NUMBER,
          isShowTellButton: work.showTelNumber,
        },
        shopWorks: formattedShopWorks(),
        shopAddress: work.shopAddress,
        clientShops: {
          totalNumber: work.hitShopWorkCount,
          shopInformations,
        },
        secondaryContentAsync,
      };
      return formattedWork;
    });

    return formattedData;
  }, [worksData, params, isLogin, secondaryContentAsync]);

  return { workWithShopWorks: formatData() };
};

// paramsにある求人の中から、docIdの一致する求人のAggregateKeyを取り出す
const getMatchedAggregateKey = (
  pinpointHighRankHitShopWorks: PrimaryContentPostProps["params"]["pinpoint_high_rank_hit_shop_works"],
  searchHitShopWorks: PrimaryContentPostProps["params"]["search_hit_shop_works"],
  docId: string,
): string | null => {
  const matchedAggregateKey = (
    shopWorks: PrimaryContentPostProps["params"]["search_hit_shop_works"],
  ) => {
    const docIds = shopWorks.filter((item) => {
      return item.inner_hits_doc_ids.filter((innerItem) => {
        return innerItem === docId;
      }).length;
    });
    // docIdの一致するものが複数存在することはない
    return docIds.length ? docIds[0].aggregate_key : null;
  };
  const matchedSearchHitShopWorksDocAggregateKey = matchedAggregateKey(searchHitShopWorks);
  if (!pinpointHighRankHitShopWorks) return matchedSearchHitShopWorksDocAggregateKey;
  const matchedPinpointHighRankWorksAggregateKey = matchedAggregateKey(
    pinpointHighRankHitShopWorks,
  );
  return matchedPinpointHighRankWorksAggregateKey || matchedSearchHitShopWorksDocAggregateKey;
};
